import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { favoritesAPI } from "@/api/favorites";
import { FavoriteEntityType } from "@/types/favorites";

// Types for favorites state
type FavoritesByType = {
  device: Set<number>;
  organization: Set<number>;
  dashboard: Set<number>;
  geozone: Set<number>;
  device_usage_profile: Set<number>;
};

type InitializedState = {
  device: boolean;
  organization: boolean;
  dashboard: boolean;
  geozone: boolean;
  device_usage_profile: boolean;
};

interface FavoritesContextValue {
  favoritesByType: FavoritesByType;
  setFavoritesByType: React.Dispatch<React.SetStateAction<FavoritesByType>>;
  loading: boolean;
  initialized: InitializedState;
  loadFavoriteIds: (entityType: FavoriteEntityType) => Promise<void>;
  isFavorite: (entityType: FavoriteEntityType, entityId: number | string) => boolean;
  toggleFavorite: (entityType: FavoriteEntityType, entityId: number | string) => Promise<boolean>;
  getFavoriteCount: (entityType: FavoriteEntityType) => number;
  clearFavorites: (entityType?: FavoriteEntityType | null) => void;
  reloadFavorites: (entityType: FavoriteEntityType) => Promise<void>;
  reloadAllFavorites: () => Promise<void>;
  refreshTrigger: number;
}

const FavoritesContext = createContext<FavoritesContextValue | undefined>(undefined);

export const useFavorites = (): FavoritesContextValue => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return context;
};

interface FavoritesProviderProps {
  children: ReactNode;
}

export const FavoritesProvider = ({ children }: FavoritesProviderProps): JSX.Element => {
  const [favoritesByType, setFavoritesByType] = useState<FavoritesByType>({
    device: new Set(),
    organization: new Set(),
    dashboard: new Set(),
    geozone: new Set(),
    device_usage_profile: new Set(),
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<InitializedState>({
    device: false,
    organization: false,
    dashboard: false,
    geozone: false,
    device_usage_profile: false,
  });
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Load favorite IDs for a specific entity type
  const loadFavoriteIds = useCallback(
    async (entityType: FavoriteEntityType): Promise<void> => {
      if (initialized[entityType]) return;

      try {
        setLoading(true);
        const response = await favoritesAPI.getFavoriteEntityIds(entityType);
        const favoriteIds = new Set(response.data);

        setFavoritesByType((prev) => ({
          ...prev,
          [entityType]: favoriteIds,
        }));

        setInitialized((prev) => ({
          ...prev,
          [entityType]: true,
        }));
      } catch (error) {
        console.error(`Failed to load ${entityType} favorites:`, error);
      } finally {
        setLoading(false);
      }
    },
    [initialized]
  );

  // Check if an entity is favorited
  const isFavorite = useCallback(
    (entityType: FavoriteEntityType, entityId: number | string): boolean => {
      return favoritesByType[entityType].has(Number(entityId));
    },
    [favoritesByType]
  );

  // Toggle favorite status
  const toggleFavorite = useCallback(
    async (entityType: FavoriteEntityType, entityId: number | string): Promise<boolean> => {
      const numericId = Number(entityId);
      const wasIsFavorite = favoritesByType[entityType].has(numericId);

      // Optimistic update
      setFavoritesByType((prev) => {
        const newSet = new Set(prev[entityType]);
        if (wasIsFavorite) {
          newSet.delete(numericId);
        } else {
          newSet.add(numericId);
        }
        return {
          ...prev,
          [entityType]: newSet,
        };
      });

      try {
        const response = await favoritesAPI.toggle(entityType, entityId);
        const isNowFavorite = response.data as unknown as boolean;

        // Update with server response
        setFavoritesByType((prev) => {
          const newSet = new Set(prev[entityType]);
          if (isNowFavorite) {
            newSet.add(numericId);
          } else {
            newSet.delete(numericId);
          }
          return {
            ...prev,
            [entityType]: newSet,
          };
        });

        return isNowFavorite;
      } catch (error) {
        // Revert optimistic update on error
        setFavoritesByType((prev) => {
          const newSet = new Set(prev[entityType]);
          if (wasIsFavorite) {
            newSet.add(numericId);
          } else {
            newSet.delete(numericId);
          }
          return {
            ...prev,
            [entityType]: newSet,
          };
        });

        console.error("Failed to toggle favorite:", error);
        throw error;
      }
    },
    [favoritesByType]
  );

  // Get count of favorites by type
  const getFavoriteCount = useCallback(
    (entityType: FavoriteEntityType): number => {
      return favoritesByType[entityType].size;
    },
    [favoritesByType]
  );

  // Clear favorites for a specific type (useful for logout/user change)
  const clearFavorites = useCallback((entityType: FavoriteEntityType | null = null): void => {
    if (entityType) {
      setFavoritesByType((prev) => ({
        ...prev,
        [entityType]: new Set(),
      }));
      setInitialized((prev) => ({
        ...prev,
        [entityType]: false,
      }));
    } else {
      setFavoritesByType({
        device: new Set(),
        organization: new Set(),
        dashboard: new Set(),
        geozone: new Set(),
        device_usage_profile: new Set(),
      });
      setInitialized({
        device: false,
        organization: false,
        dashboard: false,
        geozone: false,
        device_usage_profile: false,
      });
    }
  }, []);

  // Reload favorites for a specific type (forces refresh from server)
  const reloadFavorites = useCallback(async (entityType: FavoriteEntityType): Promise<void> => {
    try {
      setLoading(true);
      const response = await favoritesAPI.getFavoriteEntityIds(entityType);
      const favoriteIds = new Set(response.data);

      setFavoritesByType((prev) => ({
        ...prev,
        [entityType]: favoriteIds,
      }));

      setInitialized((prev) => ({
        ...prev,
        [entityType]: true,
      }));
    } catch (error) {
      console.error(`Failed to reload ${entityType} favorites:`, error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reload all favorites (forces refresh from server for all entity types)
  const reloadAllFavorites = useCallback(async (): Promise<void> => {
    const entityTypes: FavoriteEntityType[] = ["device", "organization", "dashboard", "geozone", "device_usage_profile"];

    try {
      setLoading(true);
      const promises = entityTypes.map(async (entityType) => {
        try {
          const response = await favoritesAPI.getFavoriteEntityIds(entityType);
          const favoriteIds = new Set(response.data);
          return { entityType, favoriteIds };
        } catch (error) {
          console.error(`Failed to reload ${entityType} favorites:`, error);
          return { entityType, favoriteIds: new Set<number>() };
        }
      });

      const results = await Promise.all(promises);

      // Update all entity types at once
      const newFavoritesByType: Partial<FavoritesByType> = {};
      const newInitialized: Partial<InitializedState> = {};

      results.forEach(({ entityType, favoriteIds }) => {
        newFavoritesByType[entityType] = favoriteIds;
        newInitialized[entityType] = true;
      });

      setFavoritesByType((prev) => ({
        ...prev,
        ...newFavoritesByType,
      }) as FavoritesByType);

      setInitialized((prev) => ({
        ...prev,
        ...newInitialized,
      }) as InitializedState);

      // Trigger refresh for components that depend on favorites
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to reload favorites:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const value: FavoritesContextValue = {
    favoritesByType,
    setFavoritesByType,
    loading,
    initialized,
    loadFavoriteIds,
    isFavorite,
    toggleFavorite,
    getFavoriteCount,
    clearFavorites,
    reloadFavorites,
    reloadAllFavorites,
    refreshTrigger,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
